import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { id } from 'date-fns/locale'
import pb from '@/lib/pocketbase'
import type { KegiatanLaporan } from '@/types/pocketbase'
import Card from '@/components/ui/Card'
import Badge from '@/components/ui/Badge'
import EmptyState from '@/components/ui/EmptyState'
import Button from '@/components/ui/Button'

export default function LaporanViewerPage() {
  const { id: laporanId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: laporan, isLoading } = useQuery({
    queryKey: ['laporan', 'viewer', laporanId],
    queryFn: () => pb.collection('kegiatan_laporan').getOne(laporanId!),
    enabled: !!laporanId,
  }) as { data: KegiatanLaporan | undefined; isLoading: boolean }

  if (isLoading) {
    return (
      <div className="py-4 space-y-3">
        <div className="bg-white rounded-card h-64 animate-pulse" />
      </div>
    )
  }

  if (!laporan) {
    return (
      <div className="py-4">
        <EmptyState
          title="Laporan tidak ditemukan"
          action={{ label: 'Kembali', onClick: () => navigate('/laporan') }}
        />
      </div>
    )
  }

  if (!laporan.published) {
    return (
      <div className="py-4">
        <EmptyState
          title="Laporan tidak dipublish"
          description="Laporan ini belum dipublish dan tidak dapat dilihat."
          action={{ label: 'Kembali', onClick: () => navigate('/laporan') }}
        />
      </div>
    )
  }

  if (laporan.visibilitas !== 'publik') {
    return (
      <div className="py-4">
        <EmptyState
          title="Akses dibatasi"
          description="Laporan ini hanya dapat dilihat oleh grupo tertentu."
          action={{ label: 'Kembali', onClick: () => navigate('/laporan') }}
        />
      </div>
    )
  }

  const isKeuangan = laporan.jenis === 'keuangan'

  return (
    <div className="py-4 space-y-4">
      <Card>
        <div className="flex gap-2 mb-3">
          <Badge variant="primary">{laporan.jenis}</Badge>
          {laporan.published && <Badge variant="green">Published</Badge>}
        </div>
        <h1 className="text-lg font-bold text-text-primary mb-2">{laporan.judul}</h1>
        <p className="text-xs text-text-secondary">
          {format(new Date(laporan.created), 'EEEE, dd MMMM yyyy', { locale: id })}
          {(laporan.expand?.kegiatan as { judul?: string })?.judul && (
            <> — {(laporan.expand?.kegiatan as { judul?: string }).judul}</>
          )}
        </p>
      </Card>

      <Card>
        {isKeuangan ? (
          <div>
            <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-btn mb-4">
              <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-sm text-text-secondary">
                Laporan keuangan terkunci. Hubungi Stadion untuk detail.
              </span>
            </div>
            <p className="text-sm text-text-secondary whitespace-pre-line">
              {laporan.konten.slice(0, 100)}...
            </p>
          </div>
        ) : (
          <div
            className="text-sm text-text-primary whitespace-pre-line"
            dangerouslySetInnerHTML={{ __html: laporan.konten }}
          />
        )}
      </Card>

      <Button
        block
        variant="outline"
        onClick={() => navigate('/laporan')}
      >
        Kembali
      </Button>
    </div>
  )
}
